import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Gavel, Eye, Trophy, Clock, CheckCircle2, Plus, Package } from "lucide-react";

const TILE_SIZES = ["300x300mm", "300x600mm", "600x600mm", "600x1200mm", "800x800mm", "1200x1800mm"];
const FINISHES = ["Glossy", "Matte", "Polished", "Rustic", "Lappato", "Sugar", "Carving"];

export default function VendorRFQ() {
  const { tenant, user } = useAuth();
  const [rfqs, setRfqs] = useState([]);
  const [activeRfq, setActiveRfq] = useState(null);
  const [detailRfq, setDetailRfq] = useState(null);
  const [bid, setBid] = useState({ price_per_box: "", delivery_days: "", notes: "" });
  const [newOfferOpen, setNewOfferOpen] = useState(false);
  const [offer, setOffer] = useState({ title: "", tile_size: TILE_SIZES[2], finish: FINISHES[0], quantity_boxes: "", target_price: "", notes: "" });
  const [savingOffer, setSavingOffer] = useState(false);

  const load = async () => {
    const { data } = await api.get("/rfq");
    setRfqs(data);
  };
  useEffect(() => { load(); }, []);

  const submitBid = async () => {
    try {
      await api.post(`/rfq/${activeRfq.id}/bid`, { price_per_box: Number(bid.price_per_box), delivery_days: Number(bid.delivery_days), notes: bid.notes });
      toast.success("Bid placed");
      setActiveRfq(null); setBid({ price_per_box: "", delivery_days: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitOffer = async () => {
    if (!offer.title || !offer.quantity_boxes) { toast.error("Title and quantity are required."); return; }
    setSavingOffer(true);
    try {
      await api.post("/rfq", {
        title: offer.title,
        tile_size: offer.tile_size,
        finish: offer.finish,
        quantity_boxes: Number(offer.quantity_boxes),
        target_price: offer.target_price ? Number(offer.target_price) : null,
        notes: offer.notes,
      });
      toast.success("Offer posted — buyers can now see it in the marketplace.");
      setNewOfferOpen(false);
      setOffer({ title: "", tile_size: TILE_SIZES[2], finish: FINISHES[0], quantity_boxes: "", target_price: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post offer"); }
    finally { setSavingOffer(false); }
  };

  // Detect whether the current vendor already bid on this RFQ and which is theirs.
  const myTenantId = tenant?.id || user?.tenant_id;
  const findMyBid = (r) => (r.bids || []).find((b) => b.tenant_id === myTenantId || b.vendor_tenant_id === myTenantId || b.vendor_id === myTenantId);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="vendor-rfq">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">RFQs &amp; reverse bidding</h1>
          <p className="text-muted-foreground">Bid on open buyer requests, or post a proactive offer of your own.</p>
        </div>
        <Button onClick={() => setNewOfferOpen(true)} data-testid="new-offer-btn">
          <Plus className="w-4 h-4 mr-1" /> New offer
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {rfqs.map((r) => {
          const myBids = r.bids || [];
          const lowest = myBids.reduce((acc, b) => (!acc || b.price_per_box < acc.price_per_box) ? b : acc, null);
          const myBid = findMyBid(r);
          const isOpen = r.status === "OPEN";
          const isAwarded = r.status === "AWARDED";
          const iWon = isAwarded && myBid && lowest && (myBid.id === lowest.id);
          return (
            <div key={r.id} className="surface-card p-5" data-testid={`rfq-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-overline">{r.tile_size} · {r.finish}</div>
                  <div className="font-display text-lg font-black mt-1">{r.title}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge variant="outline" className={`font-mono text-[10px] ${isOpen ? "border-emerald-500 text-emerald-700" : isAwarded ? "border-amber-500 text-amber-700" : ""}`}>{r.status}</Badge>
                  {r.kind === "offer" && r.vendor_tenant_id === myTenantId && (
                    <Badge className="text-[9px] bg-orange-500 hover:bg-orange-500">Your offer</Badge>
                  )}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                <div><div className="label-overline">Qty</div><div className="font-mono">{r.quantity_boxes} boxes</div></div>
                <div><div className="label-overline">Target</div><div className="font-mono">₹{r.target_price || "—"}</div></div>
                <div><div className="label-overline">Bids</div><div className="font-mono">{myBids.length}</div></div>
              </div>
              {r.notes && <div className="mt-3 text-sm text-muted-foreground">{r.notes}</div>}
              {lowest && <div className="mt-3 text-xs text-primary">Current lowest: <span className="font-mono">₹{lowest.price_per_box}/box</span> from {lowest.vendor_name}</div>}

              {myBid && (
                <div className={`mt-3 text-xs rounded-md px-3 py-2 border ${iWon ? "bg-emerald-50 border-emerald-300 text-emerald-800" : isAwarded ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-blue-50 border-blue-300 text-blue-800"}`} data-testid={`my-bid-${r.id}`}>
                  {iWon ? <Trophy className="w-3 h-3 inline mr-1" /> : isAwarded ? <Clock className="w-3 h-3 inline mr-1" /> : <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                  <strong>Your bid:</strong> ₹{myBid.price_per_box}/box · {myBid.delivery_days}d
                  {iWon && " · YOU WON 🎉"}
                  {isAwarded && !iWon && " · Not awarded"}
                  {isOpen && " · Pending review"}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {isOpen && !myBid && (
                  <Button size="sm" onClick={() => setActiveRfq(r)} data-testid={`bid-btn-${r.id}`}>
                    <Gavel className="w-4 h-4 mr-1" /> Place bid
                  </Button>
                )}
                {isOpen && myBid && (
                  <Button size="sm" variant="outline" onClick={() => setActiveRfq(r)} data-testid={`bid-btn-${r.id}`}>
                    <Gavel className="w-4 h-4 mr-1" /> Revise my bid
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setDetailRfq(r)} data-testid={`view-btn-${r.id}`}>
                  <Eye className="w-4 h-4 mr-1" /> View details
                </Button>
              </div>
            </div>
          );
        })}
        {rfqs.length === 0 && <div className="surface-card p-8 text-center text-sm text-muted-foreground col-span-2" data-testid="rfq-empty">No RFQs yet. Buyers will appear here when they post.</div>}
      </div>

      {/* Place / revise bid dialog */}
      <Dialog open={!!activeRfq} onOpenChange={() => setActiveRfq(null)}>
        <DialogContent data-testid="bid-dialog">
          <DialogHeader><DialogTitle>Place a bid for {activeRfq?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Price per box (₹)</Label><Input type="number" value={bid.price_per_box} onChange={(e) => setBid({ ...bid, price_per_box: e.target.value })} data-testid="bid-price-input" /></div>
            <div className="space-y-2"><Label>Delivery (days)</Label><Input type="number" value={bid.delivery_days} onChange={(e) => setBid({ ...bid, delivery_days: e.target.value })} data-testid="bid-days-input" /></div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={bid.notes} onChange={(e) => setBid({ ...bid, notes: e.target.value })} data-testid="bid-notes-input" /></div>
            <Button onClick={submitBid} data-testid="bid-submit-btn">Submit bid</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View details / bid-history dialog — always available regardless of RFQ status */}
      <Dialog open={!!detailRfq} onOpenChange={() => setDetailRfq(null)}>
        <DialogContent className="max-w-2xl" data-testid="rfq-detail-dialog">
          <DialogHeader><DialogTitle>{detailRfq?.title} · bid history</DialogTitle></DialogHeader>
          {detailRfq && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3 text-xs">
                <div><div className="label-overline">Status</div><Badge variant="outline">{detailRfq.status}</Badge></div>
                <div><div className="label-overline">Qty</div><div className="font-mono">{detailRfq.quantity_boxes}</div></div>
                <div><div className="label-overline">Size</div><div className="font-mono">{detailRfq.tile_size}</div></div>
                <div><div className="label-overline">Finish</div><div className="font-mono">{detailRfq.finish}</div></div>
              </div>
              {detailRfq.notes && <div className="text-sm text-muted-foreground surface-card p-3">{detailRfq.notes}</div>}
              <div>
                <div className="label-overline mb-2">All bids ({(detailRfq.bids || []).length})</div>
                {(detailRfq.bids || []).length === 0 && <div className="text-sm text-muted-foreground">No bids yet.</div>}
                <div className="space-y-2">
                  {(detailRfq.bids || []).slice().sort((a, b) => a.price_per_box - b.price_per_box).map((b, i) => {
                    const mine = b.tenant_id === myTenantId || b.vendor_tenant_id === myTenantId || b.vendor_id === myTenantId;
                    return (
                      <div key={b.id} className={`flex items-center justify-between rounded-md border p-2 text-xs ${mine ? "border-orange-500 bg-orange-50" : "border-border"}`} data-testid={`bid-row-${b.id}`}>
                        <div>
                          <div className="font-semibold">{i === 0 ? "🏆 " : ""}{b.vendor_name || "Vendor"}{mine ? " (You)" : ""}</div>
                          <div className="text-muted-foreground">₹{b.price_per_box}/box · {b.delivery_days} days</div>
                        </div>
                        {b.notes && <div className="text-[10px] text-muted-foreground max-w-[240px] truncate" title={b.notes}>{b.notes}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New offer (vendor-initiated proactive RFQ) */}
      <Dialog open={newOfferOpen} onOpenChange={setNewOfferOpen}>
        <DialogContent className="max-w-lg" data-testid="new-offer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package className="w-4 h-4 text-orange-500" /> Post a new offer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1"><Label className="text-xs">Offer title</Label>
              <Input value={offer.title} onChange={(e) => setOffer({ ...offer, title: e.target.value })} placeholder="e.g. Ivory glossy 600x600 — bulk stock available" data-testid="offer-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Tile size</Label>
                <Select value={offer.tile_size} onValueChange={(v) => setOffer({ ...offer, tile_size: v })}>
                  <SelectTrigger data-testid="offer-size-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TILE_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-xs">Finish</Label>
                <Select value={offer.finish} onValueChange={(v) => setOffer({ ...offer, finish: v })}>
                  <SelectTrigger data-testid="offer-finish-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{FINISHES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Quantity (boxes)</Label>
                <Input type="number" value={offer.quantity_boxes} onChange={(e) => setOffer({ ...offer, quantity_boxes: e.target.value })} data-testid="offer-qty-input" />
              </div>
              <div className="space-y-1"><Label className="text-xs">Price / box (₹)</Label>
                <Input type="number" value={offer.target_price} onChange={(e) => setOffer({ ...offer, target_price: e.target.value })} data-testid="offer-price-input" />
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={offer.notes} onChange={(e) => setOffer({ ...offer, notes: e.target.value })} placeholder="Ready to ship, lead time, discount tiers…" data-testid="offer-notes-input" />
            </div>
            <Button onClick={submitOffer} disabled={savingOffer} className="w-full" data-testid="offer-submit-btn">
              {savingOffer ? "Posting…" : "Post offer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
